// pages/DashboardUsers.tsx
import React from 'react';
import { Link } from 'react-router-dom';
import Button from '../components/Button';
import ConfirmationModal from '../components/ConfirmationDialoagModal';
import DataTableView from '../components/DataTableView2';
import DatePicker from '../components/DatePicker';
import Input from '../components/Input';
import Select from '../components/Select';
import CreateUser from '../components/userDetails/CreateUser';
import useDashboardUsers from '../hooks/logics/user-accounts/user-user-dashboard';
import { formatDate } from '../utils/helpers';

export type UserType = {
  createdAt: string;
  email: string;
  fullName: string;
  gender: string;
  isBlocked: boolean;
  isEmailVerified: boolean;
  phone: string;
  profileImage: string;
  updatedAt: string;
  username: string;
  _id: string;
  totalOrders: number;
  completedOrders: number;
  state: string;
  points?: number;
  referredBy?: {
    email: string | null;
    _id: string | null;
  };
};

const DashboardUsers = () => {
  const {
    users,
    totalPages,
    loading,
    user,
    isCreateUserOpen,
    setIsCreateUserOpen,
    isBlockModalVisible,
    setIsBlockModalVisible,
    isBlocking,
    key,
    search,
    setSearch,
    status,
    setStatus,
    fromDate,
    setFromDate,
    toDate,
    setToDate,
    state,
    setState,
    allStates,
    isLoadingStates,
    filterFormRef,
    searchFormRef,
    searchParams,
    updateUrlParams,
    handleSearch,
    handleFilter,
    resetFilters,
    handleOpenBlockModal,
    handleBlock,
    isApplyButtonDisabled,
    isClearButtonDisabled,
    refetchUsers,
    BLOCKED,
  } = useDashboardUsers();

  const columns = [
    {
      header: 'Username',
      className: 'md:p-0 md:pl-10',
      accessor: (user: UserType) => (
        <Link
          to={`/dashboard/users/${user?._id}`}
          className='flex items-center cursor-pointer md:!pl-5'
        >
          <div className='flex-shrink-0 w-8 h-8 border border-black rounded-full'>
            <img
              onError={(e) => {
                const target = e.target as HTMLImageElement;
                target.src =
                  'https://www.pngfind.com/pngs/m/610-6104451_image-placeholder-png-user-profile-placeholder-image-png.png';
              }}
              className='w-full h-full rounded-full object-cover'
              src={user?.profileImage}
              alt={user?.fullName}
            />
          </div>
          <div className='w-full'>
            <p className='ml-2 truncate max-w-[180px] text-md text-black font-normal'>
              {user?.fullName}
            </p>
          </div>
        </Link>
      ),
    },
    {
      header: 'Email',
      accessor: (user: UserType) => (
        <p className='max-w-[240px] text-ellipsis whitespace-nowrap text-black font-normal flex-shrink-0 truncate mr-2 text-md'>
          {user?.email}
        </p>
      ),
    },
    {
      header: 'Phone',
      accessor: (user: UserType) => (
        <div className='md:w-[200px]'>{user?.phone}</div>
      ),
    },
    {
      header: 'Referred by',
      className: 'md:pr-10',
      accessor: (user: UserType) =>
        user.referredBy?._id && user.referredBy.email ? (
          <Link to={`/dashboard/users/${user?.referredBy?._id}`}>
            <p>{user?.referredBy?.email}</p>
          </Link>
        ) : (
          <p>N/A</p>
        ),
    },
    {
      header: 'State',
      accessor: (user: UserType) => (
        <div className='md:w-[100px]'>{user?.state || 'N/A'}</div>
      ),
    },
    {
      header: 'Referrer points',
      accessor: (user: UserType) => (
        <div className='md:w-[150px]'>{user?.points || 'N/A'}</div>
      ),
    },
    {
      header: 'Total orders',
      accessor: (user: UserType) => (
        <div className='md:w-[150px]'>{user?.totalOrders || 'N/A'}</div>
      ),
    },
    {
      header: 'Completed orders',
      accessor: (user: UserType) => (
        <div className='md:w-[150px]'>{user?.completedOrders || 'N/A'}</div>
      ),
    },
    {
      header: 'Action',
      accessor: (user: UserType) => (
        <div className='flex items-center gap-4 md:pr-10'>
          <Link
            to={`/dashboard/users/${user?._id}`}
            className='text-primary cursor-pointer'
          >
            Details
          </Link>
          <span
            onClick={() => handleOpenBlockModal(user)}
            className='cursor-pointer text-red-500 font-semibold'
          >
            {user?.isBlocked ? 'Unblock' : 'Block'}
          </span>
        </div>
      ),
    },
  ];

  return (
    <>
      <div className='h-full'>
        <div className='w-full flex flex-row gap-0 md:justify-end items-center mb-4'>
          <form
            className='w-full flex flex-col md:flex-row gap-4'
            ref={searchFormRef}
            onSubmit={handleSearch}
          >
            <div className='w-full flex flex-row gap-x-2 md:gap-x-4 md:justify-end'>
              <Input
                onChange={(e) => setSearch(e.target.value)}
                name='search'
                value={search}
                placeholder='Search user'
                containerClassName='w-2/3 h-full md:w-auto'
                outerClassName='h-full'
                className='w-full md:w-52 outline-none input-primary h-8 md:h-10 rounded-lg'
              />
              <Button
                disabled={search === ''}
                type='submit'
                className='w-1/3 md:w-32 min-h-0 h-8 md:h-10'
              >
                Search
              </Button>
            </div>
            <div className='w-full md:w-min flex flex-row gap-x-2 md:gap-x-4 md:justify-end'>
              <Button
                type='button'
                onClick={() => setIsCreateUserOpen(true)}
                className='btn-outline flex-1 md:flex-none md:w-32 min-h-0 h-8 md:h-10'
              >
                Add user
              </Button>
              <Button
                type='button'
                onClick={() => window.location.reload()}
                className='btn-outline flex-1 md:flex-none md:w-32 min-h-0 h-8 md:h-10'
              >
                Refresh
              </Button>
            </div>
          </form>
        </div>

        <form
          key={key}
          ref={filterFormRef}
          onSubmit={handleFilter}
          className='w-full flex flex-col md:flex-row gap-4 justify-end items-center mb-4'
        >
          <div className='w-full flex flex-row gap-x-2 md:flex-row md:gap-x-4 md:w-max mt-4 md:mt-0'>
            <div className='flex flex-col items-start md:items-center md:flex-row gap-y-1 w-1/2 md:w-full'>
              <label className='mr-2'>From:</label>
              <DatePicker
                placeholderText='MM/DD/YYYY'
                onChange={(e) => setFromDate(formatDate(e?.toString() || ''))}
                name='fromDate'
                selected={fromDate ? new Date(fromDate) : null}
                className='min-h-0 h-8 md:h-10 py-1'
                inputClassName='py-0 md:py-2 h-8 md:h-10'
                popperClassName='!left-7'
              />
            </div>
            <div className='flex flex-col items-start md:items-center md:flex-row gap-y-1 w-1/2 md:w-full'>
              <label className='mr-2'>To:</label>
              <DatePicker
                placeholderText='MM/DD/YYYY'
                name='toDate'
                selected={toDate ? new Date(toDate) : null}
                onChange={(e) => setToDate(formatDate(e?.toString() || ''))}
                className='min-h-0 h-8 md:h-10 py-1'
                inputClassName='py-0 md:py-2 h-8 md:h-10'
                popperClassName='!-left-7'
              />
            </div>
          </div>

          <div className='w-full md:w-auto flex gap-x-2 md:gap-x-4'>
            <Select
              options={allStates}
              name='state'
              disabled={isLoadingStates}
              value={state}
              onChange={(e) => setState(e.target.value)}
              placeholder='State'
              className='md:w-32 outline-none bg-white rounded-lg min-h-0 h-8 md:h-10 pl-4 pr-2'
              containerClassName='w-1/2 md:w-auto'
            />
            <Select
              options={[{ label: 'Blocked', value: BLOCKED }]}
              name='status'
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              placeholder='Status'
              className='md:w-32 outline-none bg-white rounded-lg min-h-0 h-8 md:h-10 pl-4 pr-2'
              containerClassName='w-1/2 md:w-auto'
            />
          </div>

          <div className='w-full md:w-auto flex gap-x-2 md:gap-x-4 mb-8 md:mb-0'>
            <Button
              type='submit'
              className='flex-1 md:flex-none md:w-28 min-h-0 h-8 md:h-10'
              disabled={isApplyButtonDisabled()}
            >
              Apply
            </Button>
            <Button
              type='button'
              disabled={isClearButtonDisabled()}
              onClick={resetFilters}
              className='flex-1 md:flex-none md:w-24 min-h-0 h-8 md:h-10'
            >
              Clear
            </Button>
          </div>
        </form>

        <DataTableView
          data={users}
          columns={columns}
          isFetching={loading}
          emptyStateMessage='No users found'
          currentPage={parseInt(searchParams.get('page') || '1')}
          setCurrentPage={(page: number) =>
            updateUrlParams({ page: page.toString() })
          }
          totalPages={totalPages}
        />
      </div>

      <ConfirmationModal
        isVisible={isBlockModalVisible}
        onHide={() => setIsBlockModalVisible(false)}
        onConfirm={handleBlock}
        heading={`Are you sure you want to ${
          user?.isBlocked ? 'unblock' : 'block'
        } this user?`}
        isLoading={isBlocking}
      />

      <CreateUser
        isVisible={isCreateUserOpen}
        onHide={() => setIsCreateUserOpen(false)}
        onSuccess={refetchUsers}
      />
    </>
  );
};

export default DashboardUsers;
